import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { useGenerateShareLink } from '@/lib/api/edgelink';
import { Loader2, QrCode } from 'lucide-react';
import { format } from 'date-fns';
import QRCode from 'qrcode';

interface ShareLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bridgeId: string;
}

type ExpiryOption = '7' | '30' | '90' | '365' | 'never';

const expiryOptions: { value: ExpiryOption; label: string }[] = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: 'never', label: 'Never expires' },
];

export default function ShareLinkDialog({
  open,
  onOpenChange,
  bridgeId,
}: ShareLinkDialogProps) {
  const generateShareLink = useGenerateShareLink();
  const [expiry, setExpiry] = useState<ExpiryOption>('30');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);

  const handleGenerate = async () => {
    const expiryDays = expiry === 'never' ? null : parseInt(expiry);
    const result = await generateShareLink.mutateAsync({
      bridgeId,
      expiryDays,
    });
    const url = `${window.location.origin}/edgelink/s/${result.token}`;
    setShareUrl(url);
    setExpiresAt(result.expiresAt);

    try {
      const qr = await QRCode.toDataURL(url, {
        width: 200,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' },
      });
      setQrDataUrl(qr);
    } catch {
      // QR generation failed — link was still created, so continue without QR
    }
  };

  const handleReset = () => {
    setShareUrl(null);
    setExpiresAt(null);
    setQrDataUrl(null);
    setShowQr(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) handleReset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#2E3835]">
          <DialogTitle>Share Status Link</DialogTitle>
          <DialogDescription className="p-0 mt-1.5">
            Generate a link anyone can use to check this node's connection status. No login required.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-4">
          {!shareUrl ? (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Link expiry</label>
                <Select value={expiry} onValueChange={(v) => setExpiry(v as ExpiryOption)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {expiryOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {expiry === 'never'
                    ? 'The link will remain valid until the bridge is deleted.'
                    : `The link will stop working after ${expiryOptions.find((o) => o.value === expiry)?.label}.`}
                </p>
              </div>

              <Button
                onClick={handleGenerate}
                disabled={generateShareLink.isPending}
                className="w-full"
              >
                {generateShareLink.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Generate Link
              </Button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Share link</label>
                <div className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="flex-1 text-sm font-mono text-zinc-300 break-all">
                      {shareUrl}
                    </span>
                    <CopyButton textToCopy={shareUrl} label="Share Link" />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => setShowQr(!showQr)}
                    >
                      <QrCode className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {expiresAt
                    ? `Expires ${format(new Date(expiresAt), 'PPP')}`
                    : 'This link never expires.'}
                </p>
              </div>

              {showQr && qrDataUrl && (
                <div className="flex justify-center py-2">
                  <img src={qrDataUrl} alt="QR Code" className="rounded-lg" width={200} height={200} />
                </div>
              )}

              <Button variant="outline" onClick={handleReset} className="w-full">
                Generate New Link
              </Button>
            </div>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-[#2E3835]">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
